// Load only the skin-needs video suited to the current viewport.
if (!customElements.get('anso-collection-scene')) {
  customElements.define('anso-collection-scene', class extends HTMLElement {
    connectedCallback() {
      this.hero = this.querySelector('.anso-collection-hero--potrzeby');
      if (!this.hero) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const header = document.querySelector('.section-header');
      const bars = [...document.querySelectorAll('.shopify-section-group-header-group')].filter(el => el !== header);
      const fit = () => {
        const headerHeight = header?.offsetHeight || 0;
        const barHeight = bars.reduce((height, el) => height + el.offsetHeight, 0);
        document.documentElement.style.setProperty('--anso-scene-header-height', `${headerHeight}px`);
        this.hero.style.setProperty('--anso-scene-space', `calc(100svh - ${barHeight}px)`);
      };
      fit();
      window.addEventListener('resize', fit, { passive: true, signal });
      this.resizeObserver = new ResizeObserver(fit);
      [header, ...bars].filter(Boolean).forEach(el => this.resizeObserver.observe(el));
      this.media = this.querySelector('.anso-collection-hero__media');
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.failed = false;
      this.paused = false;
      this.playRevision = 0;
      this.toggle = this.querySelector('[data-motion-toggle]');
      this.video.controls = false;
      this.video.setAttribute('aria-hidden', 'true');
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.video.paused;
        this.syncMotion();
      }, { signal });
      this.video.muted = true;
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncMotion();
      });
      this.observer.observe(this.hero);
      this.motion.addEventListener('change', () => {
        this.syncMotion();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.syncMotion(), { signal });
      this.video.addEventListener('playing', () => {
        this.media.dataset.videoReady = '';
        this.updateToggle();
      }, { signal });
      this.video.addEventListener('pause', () => this.updateToggle(), { signal });
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.video.pause();
        delete this.media.dataset.videoReady;
        this.updateToggle();
      }, { signal });
      if (this.video.dataset.landscapeSrc) {
        this.mobile = matchMedia('(hover: none) and (pointer: coarse)');
        this.portrait = matchMedia('(max-aspect-ratio: 1/1)');
        this.compact = matchMedia('(max-width: 1280px)');
        this.mobile.addEventListener('change', () => this.syncSource(), { signal });
        this.portrait.addEventListener('change', () => this.syncSource(), { signal });
        this.compact.addEventListener('change', () => this.syncSource(), { signal });
        this.syncSource();
      }
      this.updateToggle();
    }
    syncSource() {
      // A tall desktop window must keep the desktop film, regardless of its size.
      const mobile = this.mobile.matches;
      const portrait = mobile && this.portrait.matches;
      const data = this.video.dataset;
      const source = !mobile && data.desktopSrc ? data.desktopSrc : portrait ? data.portraitSrc :
        (mobile && this.compact.matches ? data.compactSrc : data.landscapeSrc);
      this.hero.dataset.videoLayout = portrait ? 'portrait' : 'landscape';
      if (source === this.sourceUrl) return;
      this.sourceUrl = source;
      // Invalidate pending play() calls before replacing the resource.
      this.playRevision++;
      this.video.pause();
      this.failed = false;
      delete this.media.dataset.videoReady;
      this.video.poster = !mobile && data.desktopPoster ? data.desktopPoster :
        (portrait ? data.portraitPoster : data.landscapePoster);
      this.video.src = source;
      this.video.load();
      this.syncMotion();
    }
    updateToggle() {
      if (!this.toggle) return;
      this.toggle.hidden = this.motion.matches || this.failed;
      this.toggle.textContent = this.video.paused ? this.toggle.dataset.play : this.toggle.dataset.pause;
    }
    async syncMotion() {
      if (!this.video || this.failed) return;
      const revision = ++this.playRevision;
      const shouldPlay = () => this.isConnected && this.visible &&
        !document.hidden && !this.motion.matches && !this.paused;
      this.updateToggle();
      if (!shouldPlay()) {
        this.video.pause();
        this.updateToggle();
        return;
      }
      try {
        await this.video.play();
        if (!shouldPlay()) this.video.pause();
      } catch (error) {
        // Source changes and visibility changes may abort an older play request.
        if (revision !== this.playRevision || error.name === 'AbortError') return;
        // Keep the current video frame visible if the browser blocks autoplay.
        this.paused = true;
        this.updateToggle();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.video?.pause();
      document.documentElement.style.removeProperty('--anso-scene-header-height');
    }
  });
}

// A single navigation model serves desktop and mobile, including keyboard users.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.anso-nav-toggle');
  document.querySelectorAll('.anso-nav-toggle[aria-expanded="true"]').forEach((button) => {
    if (button !== toggle && !button.closest('.anso-nav-item').contains(event.target)) {
      button.setAttribute('aria-expanded', 'false');
      document.getElementById(button.getAttribute('aria-controls')).hidden = true;
    }
  });
  if (toggle) {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    document.getElementById(toggle.getAttribute('aria-controls')).hidden = expanded;
  }
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const item = event.target.closest('.anso-nav-item');
  const toggle = item?.querySelector('.anso-nav-toggle[aria-expanded="true"]');
  if (toggle) {
    event.stopPropagation();
    toggle.click();
    toggle.focus();
  }
}, true);
document.addEventListener('focusin', (event) => {
  document.querySelectorAll('.anso-nav-toggle[aria-expanded="true"]').forEach((button) => {
    if (!button.closest('.anso-nav-item').contains(event.target)) {
      button.setAttribute('aria-expanded', 'false');
      document.getElementById(button.getAttribute('aria-controls')).hidden = true;
    }
  });
});
